import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { AnimalItem, DurumTuru } from "../../../../mocks/animals";
import {
  formatDateLabel,
  getAnimalCustomerSummary,
  getAnimalMissingShareCount,
  getAnimalPaidTotal,
  getAnimalPaymentStatus,
  getAnimalRemainingBalance,
  getAnimalTotalCharge,
  getAnimalSalesType,
  getDisplayKupeNo,
  getDisplaySiparisNo,
  hasTransportForAnimal,
} from "../../../../lib/animalLabels";
import ShareExpandPanel from "./ShareExpandPanel";
import { ANIMAL_TABLE_GRID, ANIMAL_TABLE_MIN_WIDTH } from "./tableLayout";

interface Props {
  animal: AnimalItem;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleKesimeUygun: (id: number) => void;
  onToggleTamamlandi: (id: number) => void;
  onDelete: (id: number) => void;
  onPrint?: (animal: AnimalItem) => void;
}

const durumStyle: Record<DurumTuru, string> = {
  Aktif: "bg-farm-green-pale text-farm-green",
  "Kesim Bekliyor": "bg-farm-gold-pale text-farm-gold",
  Kesildi: "bg-farm-earth-pale text-farm-earth",
  "Teslim Edildi": "bg-farm-cream text-farm-muted border border-farm-border",
};

const fmt = (n: number) =>
  n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });

function getSalesStatus(animal: AnimalItem) {
  const sold = Number(animal.satirlanHisse ?? 0);
  const total = Number(animal.toplamHisse ?? 0);

  if (sold <= 0) return { label: "SATIŞ YOK", cls: "bg-farm-earth-pale text-farm-earth" };
  if (total > 0 && sold >= total) return { label: "SATILDI", cls: "bg-farm-green-pale text-farm-green" };
  return { label: "DEVAM", cls: "bg-farm-gold-pale text-farm-gold" };
}

function getKgRefText(animal: AnimalItem) {
  const kg = animal.kg ? String(animal.kg) : "—";
  const ref = animal.referansFiyat ? fmt(animal.referansFiyat) : "—";
  return `${kg} / ${ref}`;
}

export default function AnimalRow({
  animal,
  expanded,
  onToggleExpand,
  onToggleKesimeUygun,
  onToggleTamamlandi,
  onDelete,
  onPrint,
}: Props) {
  const displayKupeNo = getDisplayKupeNo(animal);
  const displaySiparisNo = getDisplaySiparisNo(animal);
  const salesType = getAnimalSalesType(animal);
  const customerSummary = getAnimalCustomerSummary(animal);
  const hasTransport = hasTransportForAnimal(animal);
  const toplamUcret = getAnimalTotalCharge(animal);
  const toplamOdenen = getAnimalPaidTotal(animal);
  const kalan = getAnimalRemainingBalance(animal);
  const eksikHisse = getAnimalMissingShareCount(animal);
  const salesStatus = getSalesStatus(animal);
  const paymentStatus = getAnimalPaymentStatus(animal);

  return (
    <div className="border-x border-b border-farm-border last:rounded-b-farm overflow-hidden">
      <div
        className="hidden lg:grid w-full items-center gap-1 px-2 py-2.5 text-xs hover:bg-farm-gold-pale/20 transition-colors"
        style={{ gridTemplateColumns: ANIMAL_TABLE_GRID, minWidth: `${ANIMAL_TABLE_MIN_WIDTH}px` }}
      >
        <Cell strong>{displayKupeNo}</Cell>
        <Cell>{displaySiparisNo}</Cell>

        <Cell>
          <span
            className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              animal.tur === "Büyükbaş"
                ? "bg-farm-earth-pale text-farm-earth"
                : "bg-farm-green-pale text-farm-green"
            }`}
          >
            {animal.tur}
          </span>
        </Cell>

        <Cell>
          <span className="inline-flex w-fit items-center rounded-full bg-farm-cream border border-farm-border px-2 py-0.5 text-[10px] font-semibold text-farm-brown">
            {salesType}
          </span>
        </Cell>

        <Cell>
          <span className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${salesStatus.cls}`}>
            {salesStatus.label}
          </span>
        </Cell>

        <Cell>{customerSummary}</Cell>
        <Cell>{getKgRefText(animal)}</Cell>
        <Cell className="text-right text-farm-brown font-semibold">{fmt(toplamUcret)}</Cell>
        <Cell className="text-right text-farm-green font-semibold">{fmt(toplamOdenen)}</Cell>
        <Cell className={`text-right font-semibold ${kalan > 0 ? "text-farm-earth" : "text-farm-muted"}`}>{fmt(kalan)}</Cell>
        <Cell>{formatDateLabel(animal.kesimGunu)}</Cell>

        <Cell>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              hasTransport
                ? "bg-sky-100 text-sky-700"
                : "bg-farm-cream text-farm-muted border border-farm-border"
            }`}
          >
            {hasTransport ? "Var" : "Yok"}
          </span>
        </Cell>

        <Cell>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${durumStyle[animal.durum]}`}
          >
            {animal.durum}
          </span>
        </Cell>

        <Cell>
          <button
            type="button"
            onClick={() => onToggleKesimeUygun(animal.id)}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold border ${
              animal.kesimeUygun
                ? "bg-farm-green-pale text-farm-green border-farm-green/20"
                : "bg-white text-farm-muted border-farm-border"
            }`}
          >
            <i
              className={
                animal.kesimeUygun
                  ? "ri-checkbox-circle-fill"
                  : "ri-checkbox-blank-circle-line"
              }
            />
            {animal.kesimeUygun ? "Uygun" : "Değil"}
          </button>
        </Cell>

        <Cell>
          <span
            title="Ödeme Durumu"
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold border ${
              paymentStatus === "Tamamlanan"
                ? "bg-farm-green-pale text-farm-green border-farm-green/20"
                : "bg-white text-farm-muted border-farm-border"
            }`}
          >
            <i className={paymentStatus === "Tamamlanan" ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line"} />
            {paymentStatus === "Tamamlanan" ? "Tamam" : "Bekleyen"}
          </span>
        </Cell>

        <Cell>
          <button
            type="button"
            onClick={() => onToggleTamamlandi(animal.id)}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold border ${
              animal.tamamlandi
                ? "bg-farm-gold-pale text-farm-gold border-farm-gold/20"
                : "bg-white text-farm-muted border-farm-border"
            }`}
          >
            <i
              className={
                animal.tamamlandi
                  ? "ri-checkbox-circle-fill"
                  : "ri-checkbox-blank-circle-line"
              }
            />
            {animal.tamamlandi ? "Tamam" : "Açık"}
          </button>
        </Cell>

        <Cell>
          <div className="flex items-center gap-1 justify-end whitespace-nowrap">
            <Link
              to={`/panel/hayvanlar/${animal.id}`}
              className="w-7 h-7 rounded-full border border-farm-earth/20 text-farm-earth hover:bg-farm-earth hover:text-white flex items-center justify-center"
              title="Detay"
            >
              <i className="ri-eye-line text-base" />
            </Link>

            <button
              type="button"
              onClick={() => onPrint?.(animal)}
              className="w-7 h-7 rounded-full border border-farm-border text-farm-brown hover:bg-farm-cream flex items-center justify-center"
              title="Yazdır"
            >
              <i className="ri-printer-line text-base" />
            </button>

            <button
              type="button"
              onClick={() => onDelete(animal.id)}
              className="w-7 h-7 rounded-full text-red-600 hover:bg-red-50 flex items-center justify-center"
              title="Sil"
            >
              <i className="ri-delete-bin-line text-base" />
            </button>

            <button
              type="button"
              onClick={onToggleExpand}
              className="w-7 h-7 rounded-full text-farm-muted hover:bg-farm-cream flex items-center justify-center"
              title={expanded ? "Daralt" : "Genişlet"}
            >
              <i
                className={`ri-arrow-${expanded ? "up" : "down"}-s-line text-base`}
              />
            </button>
          </div>
        </Cell>
      </div>

      <div className="lg:hidden px-3 py-3 bg-white">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-sm font-bold text-farm-brown">
              {displayKupeNo}
            </div>
            <div className="text-xs text-farm-muted mt-0.5">
              Sipariş No: {displaySiparisNo}
            </div>
          </div>

          <button
            type="button"
            onClick={onToggleExpand}
            className="w-8 h-8 rounded-full text-farm-muted hover:bg-farm-cream flex items-center justify-center"
          >
            <i
              className={`ri-arrow-${expanded ? "up" : "down"}-s-line text-base`}
            />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3 text-[11px]">
          <MiniBox label="Tip" value={salesType} />
          <MiniBox label="Satış Durumu" value={salesStatus.label} />
          <MiniBox label="Müşteri" value={customerSummary} />
          <MiniBox label="KG/REF" value={getKgRefText(animal)} />
          <MiniBox label="Satış" value={fmt(toplamUcret)} />
          <MiniBox label="Ödenen" value={fmt(toplamOdenen)} />
          <MiniBox label="Kalan" value={fmt(kalan)} />
          <MiniBox label="Kesim Gün" value={formatDateLabel(animal.kesimGunu)} />
          <MiniBox label="Taşıma" value={hasTransport ? "Var" : "Yok"} />
          <MiniBox label="Ödeme" value={paymentStatus} />
          <MiniBox label="Durum" value={animal.durum} />
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => onToggleKesimeUygun(animal.id)}
            className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border ${
              animal.kesimeUygun
                ? "bg-farm-green-pale text-farm-green border-farm-green/20"
                : "bg-white text-farm-muted border-farm-border"
            }`}
          >
            Kesime {animal.kesimeUygun ? "Uygun" : "Uygun Değil"}
          </button>

          <span
            className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border ${
              paymentStatus === "Tamamlanan"
                ? "bg-farm-green-pale text-farm-green border-farm-green/20"
                : "bg-white text-farm-muted border-farm-border"
            }`}
          >
            Ödeme {paymentStatus === "Tamamlanan" ? "Tamam" : "Bekleyen"}
          </span>

          <button
            type="button"
            onClick={() => onToggleTamamlandi(animal.id)}
            className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border ${
              animal.tamamlandi
                ? "bg-farm-gold-pale text-farm-gold border-farm-gold/20"
                : "bg-white text-farm-muted border-farm-border"
            }`}
          >
            {animal.tamamlandi ? "Tamamlandı" : "Tamamlanmadı"}
          </button>

          <Link
            to={`/panel/hayvanlar/${animal.id}`}
            className="px-2.5 py-1 rounded-full text-[10px] font-semibold border border-farm-earth/20 text-farm-earth"
          >
            Detay
          </Link>

          <button
            type="button"
            onClick={() => onPrint?.(animal)}
            className="px-2.5 py-1 rounded-full text-[10px] font-semibold border border-farm-border text-farm-brown"
          >
            Yazdır
          </button>

          <button
            type="button"
            onClick={() => onDelete(animal.id)}
            className="px-2.5 py-1 rounded-full text-[10px] font-semibold border border-red-200 text-red-600"
          >
            Sil
          </button>
        </div>

        <div className="mt-3 text-[11px] text-farm-muted">
          Hisse: <span className="font-semibold text-farm-brown">{animal.toplamHisse}/{eksikHisse}</span>
          {" • "}Toplam/Boş
        </div>
      </div>

      {expanded ? <ShareExpandPanel hisseler={animal.hisseler ?? []} /> : null}
    </div>
  );
}

function Cell({
  children,
  strong,
  className,
}: {
  children: ReactNode;
  strong?: boolean;
  className?: string;
}) {
  return (
    <div className={`min-w-0 truncate text-farm-brown ${strong ? "font-semibold" : ""} ${className ?? ""}`} title={typeof children === "string" ? children : undefined}>
      {children}
    </div>
  );
}

function MiniBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-farm-cream border border-farm-border rounded-lg px-2 py-1.5">
      <div className="text-[10px] text-farm-muted">{label}</div>
      <div className="text-farm-brown font-semibold mt-0.5 truncate">{value}</div>
    </div>
  );
}
